import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { makeStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import UserForm from './UserForm';
import Note from './layout/Note';
import PageContainer from './layout/PageContainer';
import PagePaper from './layout/PagePaper';
import Loading from './layout/Loading';
import UserDelete from './UserDelete';
import { EDIT_USER, GET_USERS, GET_USER } from '../gql/users';

const useStyle = makeStyles((theme) => ({
  noteRoot: {
    display: 'flex',
  },
  noteText: {
    flex: '1',
  },
  deleteButton: {
    top: '-8px',
  },
}));

const FormWrapper = ({ id, user, error, editUser }) => {
  const { netId, role, disabled } = user;

  const formik = useFormik({
    initialValues: {
      netId,
      role,
      active: !disabled,
    },
    validationSchema: Yup.object({
      netId: Yup.string().trim().required('Please enter NetID'),
    }),
    onSubmit: (input) => {
      // eslint-disable-next-line no-param-reassign
      input.disabled = !input.active;
      // eslint-disable-next-line no-param-reassign
      input.active = undefined;
      editUser({ variables: { id, input } });
    },
  });
  return <UserForm formik={formik} error={error} />;
};

const UserEdit = () => {
  const history = useHistory();
  const { id } = useParams();
  const classes = useStyle();
  const { loading, error, data } = useQuery(GET_USER, {
    variables: { id },
  });
  const [openDelete, setOpenDelete] = useState(false);
  const [editUser, { error: editError }] = useMutation(EDIT_USER, {
    refetchQueries: [{ query: GET_USERS }],
    onCompleted() {
      history.push('/users');
    },
  });

  if (loading) return <Loading />;
  if (error) {
    return `Error! ${error}`;
  }

  return (
    <>
      <PageContainer title="Edit User">
        <div className={classes.noteRoot}>
          <Note className={classes.noteText}>Input info to edit user</Note>
          <Button
            variant="outlined"
            color="secondary"
            className={classes.deleteButton}
            onClick={() => {
              setOpenDelete(true);
            }}
          >
            Delete
          </Button>
        </div>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <PagePaper>
              <FormWrapper
                id={id}
                editUser={editUser}
                error={editError}
                user={data.user}
              />
            </PagePaper>
          </Grid>
        </Grid>
      </PageContainer>
      <UserDelete open={openDelete} setOpen={setOpenDelete} user={data.user} />
    </>
  );
};

FormWrapper.propTypes = {
  id: PropTypes.string.isRequired,
  user: PropTypes.shape({
    netId: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.any.isRequired,
  editUser: PropTypes.func.isRequired,
};

export default UserEdit;
